/*
 * Fullscreen mobile product-image viewer.
 *
 * Opens fit-to-viewport, pinch-zooms, pans while zoomed, and swipes between
 * images when it is not. Mobile only — Dawn's own lightbox is left alone at
 * desktop widths.
 *
 * 🔑 Gestures are read from raw pointer events and applied as a CSS transform,
 * NOT handed to the browser. That is why the site-wide
 * `html, body { touch-action: pan-x pan-y }` rule (which disables the browser's
 * pinch) needed no exception: it governs the browser's gesture handling, and
 * this never uses it. `touch-action: none` on the viewer routes everything here.
 */
(function () {
  var MOBILE_MAX = 749;
  var MAX_SCALE = 4;
  var MIN_SCALE = 1;
  var DOUBLE_TAP_SCALE = 2.5;
  var SWIPE_PX = 40; // horizontal travel before a drag counts as a swipe
  var DOUBLE_TAP_MS = 300;
  var DOUBLE_TAP_SLOP = 30;

  var viewer = null;
  var track = null;
  var counter = null;
  var slides = [];
  var index = 0;
  var lastFocus = null;

  // gesture state
  var pointers = new Map();
  var mode = null; // 'swipe' | 'pan' | 'pinch'
  var startDist = 0;
  var startScale = 1;
  var startTx = 0;
  var startTy = 0;
  var startMid = { x: 0, y: 0 };
  var dragStart = { x: 0, y: 0 };
  var dragDx = 0;
  var lastTap = { t: 0, x: 0, y: 0 };

  // per-slide zoom state, reset when the slide changes
  var zoom = { scale: 1, tx: 0, ty: 0 };

  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  function currentImg() {
    return slides[index] ? slides[index].querySelector('.rc-viewer__img') : null;
  }

  function applyZoom(animate) {
    var img = currentImg();
    if (!img) return;
    img.style.transition = animate ? 'transform 0.2s ease' : 'none';
    img.style.transform =
      'translate(' + zoom.tx + 'px, ' + zoom.ty + 'px) scale(' + zoom.scale + ')';
  }

  function resetZoom(animate) {
    zoom.scale = 1;
    zoom.tx = 0;
    zoom.ty = 0;
    applyZoom(animate);
  }

  /* Pan limits: with transform-origin at the centre, scaling by s makes the
     content s times its laid-out box about that centre, so the image may be
     moved by at most half the overflow in each axis. At scale 1 the limit is 0,
     which is what makes a drag at rest unambiguously a swipe. */
  function clampPan() {
    var img = currentImg();
    if (!img) return;
    var w = img.clientWidth;
    var h = img.clientHeight;
    var maxX = Math.max(0, (w * zoom.scale - viewer.clientWidth) / 2);
    var maxY = Math.max(0, (h * zoom.scale - viewer.clientHeight) / 2);
    zoom.tx = Math.min(maxX, Math.max(-maxX, zoom.tx));
    zoom.ty = Math.min(maxY, Math.max(-maxY, zoom.ty));
  }

  function setTrack(dx, animate) {
    track.style.transition = animate ? 'transform 0.25s ease' : 'none';
    track.style.transform = 'translateX(' + (-index * viewer.clientWidth + (dx || 0)) + 'px)';
  }

  function updateCounter() {
    if (counter) counter.textContent = index + 1 + ' / ' + slides.length;
  }

  function goTo(i, animate) {
    index = Math.min(slides.length - 1, Math.max(0, i));
    resetZoom(false);
    setTrack(0, animate !== false);
    updateCounter();
  }

  /* 🔑 The centre MUST come from the slide, not the image.
     `getBoundingClientRect()` on the image reports its CURRENT, already
     transformed box — and during a pinch this runs after the state variables
     have been reset to the gesture's start values but BEFORE that transform has
     been re-applied, so the "centre" is read off the previous frame. The error
     compounds across the gesture: measured on staging, a pinch to 3.25x left
     the photo at left 439 / top 585, covering 0x259 of a 390x844 screen — i.e.
     it flew off the viewport.

     The slide is a flex child and is never transformed itself (the track is,
     but that places the active slide exactly over the viewport), and the image
     is centred inside it by `object-fit: contain` plus flex centring. So the
     slide's centre IS the image's centre at scale 1, and it is stable no matter
     what transform the image currently carries. Verified: at rest the image
     measures l=0 t=162 w=390 h=520 in an 844-tall slide, giving 195/422 either
     way. */
  function slideCentre() {
    var slide = slides[index];
    if (!slide) return { x: viewer.clientWidth / 2, y: viewer.clientHeight / 2 };
    var r = slide.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  /* Zoom about a point: keep whatever sits under the fingers where it is.
     `d` is the point's offset from the image centre in IMAGE space, so the new
     translation follows from t1 = t0 + d * (s0 - s1). */
  function zoomAround(px, py, nextScale) {
    var img = currentImg();
    if (!img) return;
    var c = slideCentre();
    var cx = c.x;
    var cy = c.y;
    var s0 = zoom.scale;
    var s1 = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
    var dx = (px - cx) / s0;
    var dy = (py - cy) / s0;
    zoom.tx = zoom.tx + dx * (s0 - s1);
    zoom.ty = zoom.ty + dy * (s0 - s1);
    zoom.scale = s1;
    clampPan();
  }

  function open(mediaId, trigger) {
    if (!viewer) return false;
    var i = slides.findIndex(function (s) {
      return s.getAttribute('data-media-id') === String(mediaId);
    });
    if (i < 0) return false; // not an image we hold — let Dawn handle it

    lastFocus = trigger || document.activeElement;
    viewer.hidden = false;
    document.body.classList.add('rc-viewer-open');
    goTo(i, false);
    var close = viewer.querySelector('[data-rc-viewer-close]');
    if (close) close.focus();
    return true;
  }

  function close() {
    if (!viewer || viewer.hidden) return;
    viewer.hidden = true;
    document.body.classList.remove('rc-viewer-open');
    resetZoom(false);
    pointers.clear();
    mode = null;
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }

  /* ---- gestures ---------------------------------------------------------- */

  function midpoint() {
    var pts = [...pointers.values()];
    return {
      x: (pts[0].x + pts[1].x) / 2,
      y: (pts[0].y + pts[1].y) / 2,
    };
  }

  function distance() {
    var pts = [...pointers.values()];
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }

  function onPointerDown(e) {
    if (viewer.hidden) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.size === 2) {
      mode = 'pinch';
      startDist = distance();
      startScale = zoom.scale;
      startTx = zoom.tx;
      startTy = zoom.ty;
      startMid = midpoint();
      return;
    }

    if (pointers.size === 1) {
      dragStart = { x: e.clientX, y: e.clientY };
      dragDx = 0;
      startTx = zoom.tx;
      startTy = zoom.ty;
      mode = zoom.scale > 1 ? 'pan' : 'swipe';

      /* double tap — only meaningful at rest or to zoom back out */
      var now = Date.now();
      if (
        now - lastTap.t < DOUBLE_TAP_MS &&
        Math.abs(e.clientX - lastTap.x) < DOUBLE_TAP_SLOP &&
        Math.abs(e.clientY - lastTap.y) < DOUBLE_TAP_SLOP
      ) {
        if (zoom.scale > 1) resetZoom(true);
        else {
          zoomAround(e.clientX, e.clientY, DOUBLE_TAP_SCALE);
          applyZoom(true);
        }
        lastTap.t = 0;
        mode = null;
        return;
      }
      lastTap = { t: now, x: e.clientX, y: e.clientY };
    }
  }

  function onPointerMove(e) {
    if (viewer.hidden || !pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (mode === 'pinch' && pointers.size >= 2) {
      var d = distance();
      if (!startDist) return;
      var next = startScale * (d / startDist);
      // recompute from the gesture's start so the zoom cannot drift
      zoom.scale = startScale;
      zoom.tx = startTx;
      zoom.ty = startTy;
      zoomAround(startMid.x, startMid.y, next);
      applyZoom(false);
      return;
    }

    if (mode === 'pan') {
      zoom.tx = startTx + (e.clientX - dragStart.x);
      zoom.ty = startTy + (e.clientY - dragStart.y);
      clampPan();
      applyZoom(false);
      return;
    }

    if (mode === 'swipe') {
      dragDx = e.clientX - dragStart.x;
      // resist at the ends so the first and last image feel bounded
      if ((index === 0 && dragDx > 0) || (index === slides.length - 1 && dragDx < 0)) {
        dragDx *= 0.35;
      }
      setTrack(dragDx, false);
    }
  }

  function onPointerUp(e) {
    if (viewer.hidden) return;
    pointers.delete(e.pointerId);

    if (mode === 'pinch') {
      /* Dropping to one finger mid-pinch would otherwise jump: re-seat the pan
         gesture on whichever pointer is left. */
      if (pointers.size === 1) {
        var left = [...pointers.values()][0];
        dragStart = { x: left.x, y: left.y };
        startTx = zoom.tx;
        startTy = zoom.ty;
        mode = zoom.scale > 1 ? 'pan' : 'swipe';
        return;
      }
      if (zoom.scale <= MIN_SCALE + 0.01) resetZoom(true);
      mode = null;
      return;
    }

    if (mode === 'swipe' && pointers.size === 0) {
      var dy = e.clientY - dragStart.y;
      if (Math.abs(dragDx) > SWIPE_PX && Math.abs(dragDx) > Math.abs(dy)) {
        goTo(index + (dragDx < 0 ? 1 : -1), true);
      } else {
        setTrack(0, true);
      }
      dragDx = 0;
    }

    if (pointers.size === 0) mode = null;
  }

  /* ---- wiring ------------------------------------------------------------ */

  /* CAPTURE phase, on the document. Dawn binds its modal opener to the button
     itself, and a capturing listener on an ancestor always runs first — the
     same mechanism the wishlist duplicate-mount fix relies on. That is what
     lets this pre-empt the lightbox without touching Dawn's element. */
  function onClickCapture(e) {
    if (!isMobile()) return;
    if (e.target.closest('quick-add-modal')) return; // quick view keeps Dawn's behaviour

    var toggle = e.target.closest('.product__media-toggle');
    var opener = toggle || e.target.closest('.product__modal-opener');
    if (!opener) return;

    var mediaId = (toggle && toggle.getAttribute('data-media-id')) || null;
    if (!mediaId) {
      var holder = opener.querySelector('[data-media-id]');
      mediaId = holder && holder.getAttribute('data-media-id');
    }
    if (!mediaId) return;

    /* open() returns false for video/3D, which this viewer does not hold — in
       that case do nothing and let Dawn's modal handle it as before. */
    if (open(mediaId, e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function init() {
    viewer = document.querySelector('[data-rc-viewer]');
    if (!viewer) return;
    track = viewer.querySelector('[data-rc-viewer-track]');
    counter = viewer.querySelector('[data-rc-viewer-counter]');
    slides = [...viewer.querySelectorAll('.rc-viewer__slide')];
    if (!track || !slides.length) return;

    document.addEventListener('click', onClickCapture, true);

    viewer.addEventListener('pointerdown', onPointerDown);
    viewer.addEventListener('pointermove', onPointerMove);
    viewer.addEventListener('pointerup', onPointerUp);
    viewer.addEventListener('pointercancel', onPointerUp);

    var closeBtn = viewer.querySelector('[data-rc-viewer-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (viewer.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') goTo(index + 1, true);
      else if (e.key === 'ArrowLeft') goTo(index - 1, true);
    });

    window.addEventListener('resize', function () {
      if (viewer.hidden) return;
      /* Width changed, so the track offset is stale. Close rather than guess if
         the viewport has grown past the mobile tier — desktop uses Dawn's own
         lightbox and this must never be left showing there. */
      if (!isMobile()) close();
      else {
        resetZoom(false);
        setTrack(0, false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
